import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import { CourtsAndAvailability } from '../components/courts';
import { api, PricingRule } from '../api/client';

// ============================================
// Icons
// ============================================

const IndoorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2L3 8v9a1 1 0 001 1h4v-5a1 1 0 011-1h2a1 1 0 011 1v5h4a1 1 0 001-1V8l-7-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const OutdoorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.93 4.93l1.41 1.41M13.66 13.66l1.41 1.41M4.93 15.07l1.41-1.41M13.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="4" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 8h16M6 2v4M14 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RulesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M2 4h14M2 9h10M2 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="14" cy="9" r="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="10" cy="14" r="2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 4h10M5 4V3a1 1 0 011-1h2a1 1 0 011 1v1M11 4v7a2 2 0 01-2 2H5a2 2 0 01-2-2V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M10 2l2 2-7 7H3v-2l7-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ============================================
// Constants
// ============================================

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const RULE_COLORS = ['#1B3A5C', '#236332', '#C46835', '#2445B3', '#1A4E26', '#8293A5'];

const PRESETS = [
  { name: 'Weekend Peak', daysOfWeek: [0, 5, 6], startTime: '06:00', endTime: '22:00' },
  { name: 'Evening Peak', daysOfWeek: [1, 2, 3, 4, 5], startTime: '17:00', endTime: '22:00' },
  { name: 'Morning Off-Peak', daysOfWeek: [1, 2, 3, 4, 5], startTime: '06:00', endTime: '09:00' },
];

const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}
TIME_OPTIONS.push('24:00');

function formatTime(t: string) {
  if (t === '24:00') return '12:00 AM';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function hhmmToMinutes(t: string) {
  if (t === '24:00') return 24 * 60;
  const [h, m] = t.split(':').map(Number);
  return (h * 60) + (m || 0);
}

function minutesToHHMM(mins: number) {
  if (mins >= 24 * 60) return '24:00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatRate(cents: number) {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

// Calendar dates are handled as 'YYYY-MM-DD' strings in the browser's local
// time, which is what a venue staffer means by "this Saturday".
function toDateInput(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayDateInput() {
  return toDateInput(new Date());
}

// Saturday and Sunday of the upcoming weekend (this one if today is Sat/Sun),
// shifted by `weeksAhead`.
function weekendDates(weeksAhead = 0) {
  const d = new Date();
  const day = d.getDay();
  const toSaturday = day === 0 ? -1 : 6 - day;
  d.setDate(d.getDate() + toSaturday + (7 * weeksAhead));
  const saturday = toDateInput(d);
  d.setDate(d.getDate() + 1);
  return { start: saturday, end: toDateInput(d) };
}

function formatDateShort(date: string, withYear: boolean) {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
}

function formatDateRange(start: string, end?: string | null) {
  const last = end || start;
  const withYear = start.slice(0, 4) !== String(new Date().getFullYear()) || last.slice(0, 4) !== start.slice(0, 4);
  if (last === start) return formatDateShort(start, withYear);
  return `${formatDateShort(start, false)} – ${formatDateShort(last, withYear)}`;
}

type DatedRuleStatus = 'ended' | 'live' | 'upcoming';

function datedRuleStatus(rule: PricingRule): DatedRuleStatus | null {
  if (!rule.specificDate) return null;
  const today = todayDateInput();
  const last = rule.endDate || rule.specificDate;
  if (last < today) return 'ended';
  if (rule.specificDate <= today) return 'live';
  return 'upcoming';
}

// ============================================
// Pricing Rules Section
// ============================================

type RuleMode = 'weekly' | 'dates';

interface RuleFormData {
  mode: RuleMode;
  specificDate: string;
  endDate: string;
  name: string;
  courtTypeFilter: string;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  rateCents: number;
  priority: number;
  isActive: boolean;
}

const EMPTY_FORM: RuleFormData = {
  mode: 'weekly', specificDate: '', endDate: '',
  name: '', courtTypeFilter: 'all', daysOfWeek: [], startTime: '17:00', endTime: '22:00', rateCents: 4000, priority: 0, isActive: true,
};

interface CourtInfo { id: string; name: string; courtType: string; }

function PricingRulesSection({ rules, onRulesChange, courts }: { rules: PricingRule[]; onRulesChange: (rules: PricingRule[]) => void; courts: CourtInfo[] }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { showConfirm, showAlert } = useModal();
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showForm) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [showForm, editingId]);

  const openNewDated = () => {
    const weekend = weekendDates();
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      mode: 'dates',
      name: 'Weekend special',
      specificDate: weekend.start,
      endDate: weekend.end,
      startTime: '06:00',
      endTime: '22:00',
    });
    setShowForm(true);
  };

  const openNew = (preset?: typeof PRESETS[0]) => {
    setEditingId(null);
    setForm(preset ? {
      ...EMPTY_FORM,
      name: preset.name,
      daysOfWeek: [...preset.daysOfWeek],
      startTime: preset.startTime,
      endTime: preset.endTime,
    } : { ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (rule: PricingRule) => {
    setEditingId(rule.id);
    setForm({
      mode: rule.specificDate ? 'dates' : 'weekly',
      specificDate: rule.specificDate || '',
      endDate: rule.endDate || rule.specificDate || '',
      name: rule.name,
      courtTypeFilter: rule.courtTypeFilter,
      daysOfWeek: [...(rule.daysOfWeek || [])],
      startTime: rule.startTime,
      endTime: rule.endTime,
      rateCents: rule.rateCents,
      priority: rule.priority,
      isActive: rule.isActive,
    });
    setShowForm(true);
  };

  const toggleDay = (day: number) => {
    setForm(f => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(day) ? f.daysOfWeek.filter(d => d !== day) : [...f.daysOfWeek, day],
    }));
  };

  const datesInvalid = form.mode === 'dates' && (
    !form.specificDate || !form.endDate || form.endDate < form.specificDate
  );
  const canSave = !!form.name.trim() && (form.mode === 'weekly' ? form.daysOfWeek.length > 0 : !datesInvalid);

  const handleSave = async () => {
    if (!canSave) return;
    const { mode, specificDate, endDate, ...rest } = form;
    // Sending daysOfWeek or specificDate tells the API which kind of rule this is.
    const payload = mode === 'dates'
      ? { ...rest, name: rest.name.trim(), daysOfWeek: null, specificDate, endDate }
      : { ...rest, name: rest.name.trim(), specificDate: null, endDate: null };
    setSaving(true);
    try {
      if (editingId) {
        const { rule } = await api.updatePricingRule(editingId, payload);
        onRulesChange(rules.map(existing => existing.id === editingId ? rule : existing));
      } else {
        const { rule } = await api.createPricingRule(payload);
        // Dated rules take precedence, so list them first like the API does.
        onRulesChange(rule.specificDate ? [rule, ...rules] : [...rules, rule]);
      }
      setShowForm(false);
    } catch (err: any) {
      await showAlert('Error', err?.message || 'Failed to save rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: PricingRule) => {
    const confirmed = await showConfirm('Delete Rule', `Delete "${rule.name}"? This cannot be undone.`, { type: 'danger', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await api.deletePricingRule(rule.id);
      onRulesChange(rules.filter(x => x.id !== rule.id));
    } catch (err: any) {
      await showAlert('Error', err?.message || 'Failed to delete', 'error');
    }
  };

  const handleToggle = async (rule: PricingRule) => {
    try {
      const { rule: updated } = await api.updatePricingRule(rule.id, { isActive: !rule.isActive });
      onRulesChange(rules.map(x => x.id === rule.id ? updated : x));
    } catch { /* ignore */ }
  };

  // The form opens where it was asked for: under the header buttons for a
  // new rule, and in place of the row being edited.
  const ruleForm = (
    <div ref={formRef} style={{ scrollMarginTop: '24px' }}>
    <div style={{
      padding: '24px', borderRadius: '16px', border: '1px solid var(--gray-200)',
      background: 'linear-gradient(180deg, var(--gray-50) 0%, white 100%)',
    }}>
      <h4 style={{ margin: '0 0 20px', fontSize: '15px', fontWeight: 600, color: 'var(--gray-900)' }}>
        {editingId ? 'Edit Rule' : form.mode === 'dates' ? 'New Temporary Price' : 'New Pricing Rule'}
      </h4>

      {/* Weekly vs. specific dates */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '6px' }}>When</label>
        <div role="radiogroup" aria-label="When the rule applies" style={{ display: 'inline-flex', gap: '4px', padding: '4px', borderRadius: '10px', background: 'var(--gray-100)' }}>
          {([['weekly', 'Every week'], ['dates', 'Specific dates']] as const).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={form.mode === mode}
              onClick={() => setForm(f => {
                if (mode === 'dates' && !f.specificDate) {
                  const weekend = weekendDates();
                  return { ...f, mode, specificDate: weekend.start, endDate: weekend.end };
                }
                return { ...f, mode };
              })}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                background: form.mode === mode ? 'white' : 'transparent',
                color: form.mode === mode ? 'var(--gray-900)' : 'var(--gray-500)',
                boxShadow: form.mode === mode ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Presets strip (only on new weekly rules) */}
      {!editingId && form.mode === 'weekly' && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {PRESETS.map(preset => (
            <button
              key={preset.name}
              onClick={() => setForm(f => ({ ...f, name: preset.name, daysOfWeek: [...preset.daysOfWeek], startTime: preset.startTime, endTime: preset.endTime }))}
              style={{
                padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 500,
                border: form.name === preset.name ? '1px solid var(--primary)' : '1px solid var(--gray-200)',
                background: form.name === preset.name ? 'var(--primary-light)' : 'white',
                color: form.name === preset.name ? 'var(--primary)' : 'var(--gray-500)',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      )}

      {/* Rule name */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Rule Name</label>
        <input
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder={form.mode === 'dates' ? 'e.g., Weekend Special' : 'e.g., Weekend Peak'}
          style={{
            width: '100%', maxWidth: '300px', padding: '8px 12px', fontSize: '14px',
            border: '1px solid var(--gray-300)', borderRadius: '8px', outline: 'none',
          }}
        />
      </div>

      {/* Dates (temporary price) */}
      {form.mode === 'dates' && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end' }}>
            <div>
              <label htmlFor="rule-start-date" style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>First Day</label>
              <input
                id="rule-start-date"
                type="date"
                value={form.specificDate}
                min={todayDateInput()}
                onChange={e => {
                  const value = e.target.value;
                  setForm(f => ({ ...f, specificDate: value, endDate: !f.endDate || f.endDate < value ? value : f.endDate }));
                }}
                style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-300)', borderRadius: '8px' }}
              />
            </div>
            <span style={{ fontSize: '14px', color: 'var(--gray-400)', paddingBottom: '10px' }}>through</span>
            <div>
              <label htmlFor="rule-end-date" style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Last Day</label>
              <input
                id="rule-end-date"
                type="date"
                value={form.endDate}
                min={form.specificDate || todayDateInput()}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-300)', borderRadius: '8px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {[['This weekend', 0], ['Next weekend', 1]].map(([label, weeks]) => {
              const weekend = weekendDates(weeks as number);
              const selected = form.specificDate === weekend.start && form.endDate === weekend.end;
              return (
                <button
                  key={label as string}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, specificDate: weekend.start, endDate: weekend.end }))}
                  style={{
                    padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                    border: selected ? '1px solid var(--primary)' : '1px solid var(--gray-200)',
                    background: selected ? 'var(--primary-light)' : 'white',
                    color: selected ? 'var(--primary)' : 'var(--gray-500)',
                  }}
                >
                  {label} ({formatDateRange(weekend.start, weekend.end)})
                </button>
              );
            })}
          </div>
          {datesInvalid && form.specificDate && form.endDate && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--danger, #B42318)' }}>The last day must be on or after the first day.</p>
          )}
        </div>
      )}

      {/* Days of week */}
      {form.mode === 'weekly' && (
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '6px' }}>Days</label>
        <div style={{ display: 'flex', gap: '6px' }}>
          {DAY_LABELS.map((label, idx) => (
            <button
              key={idx}
              onClick={() => toggleDay(idx)}
              style={{
                width: '44px', height: '36px', borderRadius: '8px', border: 'none',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                background: form.daysOfWeek.includes(idx) ? 'var(--primary)' : 'var(--gray-100)',
                color: form.daysOfWeek.includes(idx) ? 'white' : 'var(--gray-400)',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Time range */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Start Time</label>
          <select
            value={form.startTime}
            onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
            style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-300)', borderRadius: '8px' }}
          >
            {TIME_OPTIONS.filter(t => t !== '24:00').map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
          </select>
        </div>
        <span style={{ fontSize: '14px', color: 'var(--gray-400)', paddingBottom: '10px' }}>to</span>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>End Time</label>
          <select
            value={form.endTime}
            onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
            style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-300)', borderRadius: '8px' }}
          >
            {TIME_OPTIONS.filter(t => t > form.startTime).map(t => <option key={t} value={t}>{formatTime(t)}</option>)}
          </select>
        </div>
      </div>

      {/* Rate + Court type */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Hourly Rate</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--gray-400)' }}>$</span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.rateCents / 100}
              onChange={e => setForm(f => ({ ...f, rateCents: Math.round(parseFloat(e.target.value || '0') * 100) }))}
              style={{
                width: '80px', padding: '8px 10px', fontSize: '18px', fontWeight: 700,
                border: '1px solid var(--gray-300)', borderRadius: '8px', outline: 'none',
              }}
            />
            <span style={{ fontSize: '13px', color: 'var(--gray-400)' }}>/hr</span>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '4px' }}>Applies To</label>
          <select
            value={form.courtTypeFilter}
            onChange={e => setForm(f => ({ ...f, courtTypeFilter: e.target.value }))}
            style={{
              padding: '8px 12px', fontSize: '14px', borderRadius: '8px',
              border: '1px solid var(--gray-300)', background: 'white', outline: 'none',
              minWidth: '180px',
            }}
          >
            <option value="all">All Courts</option>
            <option value="indoor">All Indoor Courts</option>
            <option value="outdoor">All Outdoor Courts</option>
            {courts.length > 0 && <option disabled>───────────</option>}
            {courts.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !canSave}>
          {saving ? 'Saving...' : editingId ? 'Update Rule' : 'Create Rule'}
        </button>
        <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
    </div>
  );

  return (
    <section className="defaults-card">
      <div className="defaults-card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <h2 className="defaults-card__title">Pricing Rules <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-400)', marginLeft: '8px', letterSpacing: '0', textTransform: 'none' }}>{rules.length > 0 && `${rules.length} total`}</span></h2>
          <p className="defaults-card__subtitle">Override default rates for specific days and times, or set a temporary price for certain dates, like a weekend special. Temporary prices take priority over weekly rules and stop on their own after the last date.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={openNewDated}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <PlusIcon /> Temporary Price
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => openNew()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <PlusIcon /> Add Rule
          </button>
        </div>
      </div>

      <div>
        {showForm && !editingId && <div style={{ marginBottom: '20px' }}>{ruleForm}</div>}

        {/* Quick presets */}
        {rules.length === 0 && !showForm && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--gray-500)' }}>
              Get started with a common pricing pattern:
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {PRESETS.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => openNew(preset)}
                  style={{
                    padding: '12px 18px', borderRadius: '12px', border: '1px solid var(--gray-200)',
                    background: 'var(--gray-50)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                    transition: 'all 0.15s', fontSize: '14px', fontWeight: 500, color: 'var(--gray-700)',
                  }}
                  onMouseEnter={e => { (e.target as HTMLElement).style.borderColor = 'var(--primary)'; (e.target as HTMLElement).style.background = 'var(--primary-light)'; }}
                  onMouseLeave={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-200)'; (e.target as HTMLElement).style.background = 'var(--gray-50)'; }}
                >
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>{preset.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
                      {preset.daysOfWeek.map(d => DAY_LABELS[d]).join(', ')} · {formatTime(preset.startTime)} – {formatTime(preset.endTime)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {/* Rules list */}
        {rules.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rules.map((rule, idx) => {
              const color = RULE_COLORS[idx % RULE_COLORS.length];
              if (showForm && editingId === rule.id) return <React.Fragment key={rule.id}>{ruleForm}</React.Fragment>;
              return (
                <div
                  key={rule.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px',
                    borderRadius: '12px', border: '1px solid var(--gray-200)',
                    background: rule.isActive ? 'white' : 'var(--gray-50)',
                    opacity: rule.isActive ? 1 : 0.6, transition: 'all 0.2s',
                  }}
                >
                  {/* Color indicator */}
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--gray-900)' }}>{rule.name}</span>
                      {(() => {
                        const status = datedRuleStatus(rule);
                        if (!status) return null;
                        const styles: Record<DatedRuleStatus, { label: string; bg: string; fg: string }> = {
                          live: { label: 'Temporary · in effect', bg: 'var(--primary-light)', fg: 'var(--primary)' },
                          upcoming: { label: 'Temporary · upcoming', bg: 'var(--gray-100)', fg: 'var(--gray-600)' },
                          ended: { label: 'Temporary · ended', bg: 'var(--gray-100)', fg: 'var(--gray-400)' },
                        };
                        const st = styles[status];
                        return (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: st.bg, color: st.fg }}>
                            {st.label}
                          </span>
                        );
                      })()}
                      <span style={{
                        padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                        background: color + '18', color: color, border: `1px solid ${color}40`,
                      }}>
                        {formatRate(rule.rateCents)}/hr
                      </span>
                      {rule.courtTypeFilter !== 'all' && (
                        <span style={{
                          padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 500,
                          background: 'var(--gray-100)', color: 'var(--gray-500)',
                        }}>
                          {(() => {
                            const court = courts.find(c => c.id === rule.courtTypeFilter);
                            if (court) return court.name;
                            return rule.courtTypeFilter;
                          })()}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--gray-400)' }}>
                      {rule.specificDate ? (
                        // Dated rule (a temporary price) — no recurring days to show.
                        <span style={{
                          padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                          background: color + '18', color,
                        }}>
                          {formatDateRange(rule.specificDate, rule.endDate)}
                        </span>
                      ) : (
                        /* Day pills */
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {[0, 1, 2, 3, 4, 5, 6].map(d => (
                            <span
                              key={d}
                              style={{
                                width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 600,
                                background: (rule.daysOfWeek || []).includes(d) ? color + '20' : 'var(--gray-100)',
                                color: (rule.daysOfWeek || []).includes(d) ? color : 'var(--gray-300)',
                                border: (rule.daysOfWeek || []).includes(d) ? `1px solid ${color}40` : '1px solid transparent',
                              }}
                            >
                              {DAY_LABELS_SHORT[d]}
                            </span>
                          ))}
                        </div>
                      )}
                      <span>{formatTime(rule.startTime)} – {formatTime(rule.endTime)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(rule)}
                      aria-label={rule.isActive ? 'Disable rule' : 'Enable rule'}
                      style={{
                        width: '44px', height: '44px', borderRadius: '22px', border: 'none', cursor: 'pointer',
                        background: 'transparent', position: 'relative',
                        transition: 'background 0.2s', padding: 0, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <span style={{
                        display: 'block', width: '44px', height: '24px', borderRadius: '12px',
                        background: rule.isActive ? 'var(--primary)' : 'var(--gray-300)',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '50%', background: 'white',
                          position: 'absolute', top: '3px', transition: 'left 0.2s',
                          left: rule.isActive ? '23px' : '3px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)', display: 'block',
                        }} />
                      </span>
                    </button>
                    <button onClick={() => openEdit(rule)} aria-label="Edit rule" style={{ padding: '15px', borderRadius: '8px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                      <EditIcon />
                    </button>
                    <button onClick={() => handleDelete(rule)} aria-label="Delete rule" style={{ padding: '15px', borderRadius: '8px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Add another button inline */}
            {!showForm && (
              <button
                onClick={() => openNew()}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '14px', borderRadius: '12px', border: '2px dashed var(--gray-200)',
                  background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                  color: 'var(--gray-400)', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.target as HTMLElement).style.borderColor = 'var(--primary)'; (e.target as HTMLElement).style.color = 'var(--primary)'; }}
                onMouseLeave={e => { (e.target as HTMLElement).style.borderColor = 'var(--gray-200)'; (e.target as HTMLElement).style.color = 'var(--gray-400)'; }}
              >
                <PlusIcon /> Add another rule
              </button>
            )}
          </div>
        )}

        {/* Empty state (no rules, no presets section showing) */}
        {rules.length === 0 && !showForm && (
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--gray-400)', textAlign: 'center', padding: '8px 0' }}>
            No pricing rules yet. All courts use default hourly rates.
          </p>
        )}

      </div>
    </section>
  );
}

// ============================================
// Court Pricing Summary
// ============================================

interface CourtPricingSummaryProps {
  court: { id: string; name: string; courtType: string; sportType: string };
  rules: PricingRule[];
  defaultRateCents: number;
  ruleColorMap: Record<string, string>;
}

function CourtPricingSummary({ court, rules, defaultRateCents, ruleColorMap }: CourtPricingSummaryProps) {
  const applicableRules = useMemo(() =>
    // Dated (temporary) rules aren't part of the weekly schedule shown here.
    rules.filter(r => r.isActive && !r.specificDate && (
      r.courtTypeFilter === 'all' ||
      r.courtTypeFilter === court.courtType ||
      r.courtTypeFilter === court.id
    )),
    [rules, court.courtType, court.id]
  );

  const timelineBounds = useMemo(() => {
    const starts = applicableRules.map(rule => hhmmToMinutes(rule.startTime));
    const ends = applicableRules.map(rule => hhmmToMinutes(rule.endTime));
    const fallbackStart = 6 * 60;
    const fallbackEnd = 22 * 60;
    const min = Math.min(fallbackStart, ...starts);
    const max = Math.max(fallbackEnd, ...ends);
    const roundedStart = Math.floor(min / 60) * 60;
    const roundedEnd = Math.ceil(max / 60) * 60;
    return {
      start: roundedStart,
      end: roundedEnd,
      total: roundedEnd - roundedStart,
    };
  }, [applicableRules]);

  // Build a continuous tiled schedule per day: every minute is covered by exactly
  // one band (a rule override or the base rate). Consecutive minutes with the
  // same rate are merged into a single band.
  const schedule = useMemo(() => {
    return [1, 2, 3, 4, 5, 6, 0].map(day => {
      const dayRules = applicableRules
        // Specific-date (one-off) rules don't belong in this recurring weekly preview.
        .filter(r => (r.daysOfWeek || []).includes(day))
        .sort((a, b) => b.priority - a.priority || a.startTime.localeCompare(b.startTime));

      // Per-minute resolution across the visible timeline.
      const perMinute: Array<{ rate: number; ruleId: string | null; ruleName: string | null }>
        = new Array(timelineBounds.total);
      for (let i = 0; i < timelineBounds.total; i++) {
        perMinute[i] = { rate: defaultRateCents, ruleId: null, ruleName: null };
      }
      // Apply rules in priority order (highest last so it wins via overwrite).
      const ordered = [...dayRules].sort((a, b) => a.priority - b.priority);
      for (const rule of ordered) {
        const s = Math.max(hhmmToMinutes(rule.startTime), timelineBounds.start);
        const e = Math.min(hhmmToMinutes(rule.endTime), timelineBounds.end);
        for (let m = s; m < e; m++) {
          const idx = m - timelineBounds.start;
          perMinute[idx] = { rate: rule.rateCents, ruleId: rule.id, ruleName: rule.name };
        }
      }

      // Collapse adjacent minutes with identical (rate, ruleId) into bands.
      const bands: Array<{
        startMinutes: number;
        endMinutes: number;
        rate: number;
        ruleId: string | null;
        ruleName: string | null;
      }> = [];
      let cursor = 0;
      while (cursor < perMinute.length) {
        const head = perMinute[cursor];
        let end = cursor + 1;
        while (
          end < perMinute.length &&
          perMinute[end].rate === head.rate &&
          perMinute[end].ruleId === head.ruleId
        ) {
          end++;
        }
        bands.push({
          startMinutes: timelineBounds.start + cursor,
          endMinutes: timelineBounds.start + end,
          rate: head.rate,
          ruleId: head.ruleId,
          ruleName: head.ruleName,
        });
        cursor = end;
      }
      return { day, bands };
    });
  }, [applicableRules, defaultRateCents, timelineBounds]);

  const hasRules = applicableRules.length > 0;


  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    for (let minute = timelineBounds.start; minute <= timelineBounds.end; minute += 60) {
      marks.push(minute);
    }
    return marks;
  }, [timelineBounds]);

  return (
    <div className="card court-pricing-summary">
      <div className="court-pricing-summary__header">
        <div className="court-pricing-summary__title-wrap">
          <div className={`court-pricing-summary__icon court-pricing-summary__icon--${court.courtType === 'outdoor' ? 'outdoor' : 'indoor'}`}>
            {court.courtType === 'outdoor' ? <OutdoorIcon /> : <IndoorIcon />}
          </div>
          <div className="court-pricing-summary__title-copy">
            <h3>{court.name}</h3>
            <p>{court.courtType} court</p>
          </div>
        </div>
        <div className="court-pricing-summary__base-rate">
          <span>Base rate</span>
          <strong>{formatRate(defaultRateCents)}<em>/hr</em></strong>
        </div>
      </div>

      <div className="court-pricing-summary__body">
        <div className="court-pricing-summary__eyebrow">
          <span>Weekly Pricing Schedule</span>
          {hasRules && <span>{applicableRules.length} active override{applicableRules.length !== 1 ? 's' : ''}</span>}
        </div>

        {!hasRules ? (
          <div className="court-pricing-summary__empty">
            <p>{formatRate(defaultRateCents)}/hr every day, all hours</p>
            <span>Create pricing rules above to introduce peak and off-peak windows.</span>
          </div>
        ) : (
          <div className="gcal">
            <div className="gcal__times" aria-hidden="true">
              <div className="gcal__times-spacer" />
              {hourMarks.slice(0, -1).map((tick) => {
                const hour = Math.floor(tick / 60);
                const label = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
                return (
                  <div key={tick} className="gcal__time-label">
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>

            <div className="gcal__days">
              {schedule.map(({ day, bands }) => {
                const isWeekend = day === 0 || day === 6;
                const dayLabel = DAY_LABELS[day];
                return (
                  <div key={day} className={`gcal__day ${isWeekend ? 'gcal__day--weekend' : ''}`}>
                    <div className="gcal__day-header">
                      <span className="gcal__day-name">{dayLabel.slice(0, 3).toUpperCase()}</span>
                    </div>
                    <div className="gcal__col" style={{ ['--hour-count' as string]: (hourMarks.length - 1).toString() }}>
                      {bands.map((band, i) => {
                        const top = ((band.startMinutes - timelineBounds.start) / timelineBounds.total) * 100;
                        const height = ((band.endMinutes - band.startMinutes) / timelineBounds.total) * 100;
                        const label = `${formatTime(minutesToHHMM(band.startMinutes))}–${formatTime(minutesToHHMM(band.endMinutes))}`;
                        const isBase = !band.ruleId;
                        const ruleColor = band.ruleId ? ruleColorMap[band.ruleId] : undefined;
                        return (
                          <div
                            key={i}
                            className={`gcal__band ${isBase ? 'gcal__band--base' : 'gcal__band--rule'}`}
                            style={{
                              top: `${top}%`,
                              height: `${height}%`,
                              ...(ruleColor ? { ['--rule-color' as string]: ruleColor } : {}),
                            }}
                            title={`${isBase ? 'Base rate' : band.ruleName ?? ''} · ${label} · ${formatRate(band.rate)}/hr`}
                          >
                            <strong>{formatRate(band.rate)}</strong>
                            <span className="gcal__band-name">
                              {isBase
                                ? `Default ${court.courtType === 'outdoor' ? 'Outdoor' : 'Indoor'}`
                                : `${band.ruleName ?? 'Rule'}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function CourtsPricingScreen() {
  const { business, loading, needsVenueSelection, addCourt, updateCourt, deleteCourt, updateVenuePricing, updateBookingWindowDays } = useBusiness();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [selectedCourtId, setSelectedCourtId] = useState<string | null>(null);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);

  const [indoorRate, setIndoorRate] = useState('');
  const [outdoorRate, setOutdoorRate] = useState('');
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(false);

  const [bookingWindow, setBookingWindow] = useState(14);
  const [windowSaving, setWindowSaving] = useState(false);
  const [windowSaved, setWindowSaved] = useState(false);

  // Load pricing rules
  const loadRules = useCallback(async () => {
    try {
      const { rules: r } = await api.getPricingRules();
      setRules(r);
    } catch {
      // ignore
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  useEffect(() => {
    if (business) {
      setIndoorRate(String((business.indoorRateCents ?? 3000) / 100));
      setOutdoorRate(String((business.outdoorRateCents ?? 2000) / 100));
      setBookingWindow(business.bookingWindowDays ?? 14);
    }
  }, [business?.indoorRateCents, business?.outdoorRateCents, business?.bookingWindowDays]);

  // Auto-select first court
  useEffect(() => {
    if (business?.courts.length && !selectedCourtId) {
      setSelectedCourtId(business.courts[0].id);
    }
  }, [business?.courts, selectedCourtId]);

  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) {
      navigate('/superadmin');
    }
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  if (loading || !business) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}><div className="spinner" /></div>
      </DashboardLayout>
    );
  }

  if (needsVenueSelection) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Select a Venue</h1>
          <p>As a Super Admin, please select a venue to manage from the All Venues page.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/superadmin')}>Go to All Venues</button>
      </DashboardLayout>
    );
  }

  const handleSavePricing = async () => {
    setPricingSaving(true);
    setPricingSaved(false);
    try {
      const indoorCents = Math.round(parseFloat(indoorRate || '0') * 100);
      const outdoorCents = Math.round(parseFloat(outdoorRate || '0') * 100);
      await updateVenuePricing(indoorCents, outdoorCents);
      setPricingSaved(true);
      setTimeout(() => setPricingSaved(false), 2500);
    } catch {
      // Error handled by context
    } finally {
      setPricingSaving(false);
    }
  };

  const handleSaveBookingWindow = async () => {
    setWindowSaving(true);
    setWindowSaved(false);
    try {
      await updateBookingWindowDays(bookingWindow);
      setWindowSaved(true);
      setTimeout(() => setWindowSaved(false), 2500);
    } catch {
      // Error handled by context
    } finally {
      setWindowSaving(false);
    }
  };

  const hasIndoor = business.courts.some(c => c.courtType === 'indoor' || c.courtType === 'covered');
  const hasOutdoor = business.courts.some(c => c.courtType === 'outdoor');
  const hasCourts = business.courts.length > 0;
  const selectedCourt = business.courts.find(c => c.id === selectedCourtId);
  const activeRulesCount = rules.filter(rule => rule.isActive && datedRuleStatus(rule) !== 'ended').length;

  const indoorChanged = hasCourts && String((business.indoorRateCents ?? 3000) / 100) !== indoorRate;
  const outdoorChanged = hasCourts && String((business.outdoorRateCents ?? 2000) / 100) !== outdoorRate;
  const pricingChanged = indoorChanged || outdoorChanged;
  const windowChanged = bookingWindow !== (business.bookingWindowDays ?? 14);

  const getDefaultRate = (courtType: string) => {
    if (courtType === 'outdoor') return business.outdoorRateCents ?? 2000;
    return business.indoorRateCents ?? 3000;
  };

  return (
    <DashboardLayout>
      <div className="page-header page-header--premium">
        <div className="courts-pricing-hero">
          <div className="courts-pricing-hero__copy">
            <span className="courts-pricing-hero__eyebrow">Venue Control Center</span>
            <h1>Courts & Pricing</h1>
            <p>Shape how your venue feels to customers, from base rates to peak-hour strategy and court-by-court presentation.</p>
          </div>
          <div className="courts-pricing-hero__stats">
            <div className="courts-pricing-stat">
              <span className="courts-pricing-stat__label">Courts</span>
              <strong>{business.courts.length}</strong>
            </div>
            <div className="courts-pricing-stat">
              <span className="courts-pricing-stat__label">Active Rules</span>
              <strong>{activeRulesCount}</strong>
            </div>
            <div className="courts-pricing-stat">
              <span className="courts-pricing-stat__label">Booking Window</span>
              <strong>{bookingWindow}<span className="courts-pricing-stat__unit"> days</span></strong>
            </div>
          </div>
        </div>
      </div>

      <div className="courts-pricing-page">

        {/* Venue Defaults — consolidated: base rates + booking window */}
        {hasCourts && (
          <section className="defaults-card">
            <header className="defaults-card__header">
              <h2 className="defaults-card__title">Venue Defaults</h2>
              <p className="defaults-card__subtitle">Baseline hourly rates and how far ahead guests can book.</p>
            </header>

            <div className="defaults-card__grid">
              {(hasIndoor || !hasOutdoor) && (
                <div className="defaults-field">
                  <label className="defaults-field__label">
                    <span className="defaults-field__icon defaults-field__icon--indoor"><IndoorIcon /></span>
                    Indoor rate
                  </label>
                  <div className="defaults-field__input">
                    <span className="defaults-field__prefix">$</span>
                    <input
                      type="number" min="0" step="1" value={indoorRate}
                      onChange={(e) => setIndoorRate(e.target.value)}
                      className={indoorChanged ? 'is-dirty' : ''}
                    />
                    <span className="defaults-field__suffix">/ hr</span>
                  </div>
                </div>
              )}

              {hasOutdoor && (
                <div className="defaults-field">
                  <label className="defaults-field__label">
                    <span className="defaults-field__icon defaults-field__icon--outdoor"><OutdoorIcon /></span>
                    Outdoor rate
                  </label>
                  <div className="defaults-field__input">
                    <span className="defaults-field__prefix">$</span>
                    <input
                      type="number" min="0" step="1" value={outdoorRate}
                      onChange={(e) => setOutdoorRate(e.target.value)}
                      className={outdoorChanged ? 'is-dirty' : ''}
                    />
                    <span className="defaults-field__suffix">/ hr</span>
                  </div>
                </div>
              )}

              <div className="defaults-field defaults-field--window">
                <label className="defaults-field__label">
                  <span className="defaults-field__icon defaults-field__icon--window"><CalendarIcon /></span>
                  Booking window
                  <strong className="defaults-field__value">{bookingWindow} {bookingWindow === 1 ? 'day' : 'days'}</strong>
                </label>
                <div className="defaults-field__slider">
                  <input
                    type="range" min={1} max={14} value={bookingWindow}
                    onChange={(e) => setBookingWindow(Number(e.target.value))}
                    style={{ ['--val' as string]: String(bookingWindow) }}
                  />
                  <div className="defaults-field__scale"><span>1</span><span>14</span></div>
                </div>
              </div>
            </div>

            <footer className="defaults-card__footer">
              {(pricingSaved || windowSaved) && (
                <div className="courts-pricing-status-pill">
                  <CheckIcon /> Saved
                </div>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  if (pricingChanged) await handleSavePricing();
                  if (windowChanged) await handleSaveBookingWindow();
                }}
                disabled={(pricingSaving || windowSaving) || (!pricingChanged && !windowChanged)}
              >
                {(pricingSaving || windowSaving) ? 'Saving…' : 'Save changes'}
              </button>
            </footer>
          </section>
        )}

        {/* Pricing Rules */}
        {hasCourts && (
          <PricingRulesSection
            rules={rules}
            onRulesChange={setRules}
            courts={business.courts.map(c => ({ id: c.id, name: c.name, courtType: c.courtType }))}
          />
        )}

        {/* Courts + Summary two-column layout */}
        <div className="courts-pricing-layout">
          <div className="courts-pricing-layout__courts">
            <CourtsAndAvailability
              courts={business.courts}
              operatingHours={business.operatingHours}
              onAddCourt={addCourt}
              onUpdateCourt={updateCourt}
              onDeleteCourt={deleteCourt}
              selectedCourtId={selectedCourtId}
              onSelectCourt={setSelectedCourtId}
            />
          </div>

          <div className="courts-pricing-layout__summary">
            {selectedCourt && !rulesLoading && (
              <CourtPricingSummary
                court={selectedCourt}
                rules={rules}
                defaultRateCents={getDefaultRate(selectedCourt.courtType)}
                ruleColorMap={Object.fromEntries(rules.map((r, i) => [r.id, RULE_COLORS[i % RULE_COLORS.length]]))}
              />
            )}
            {!selectedCourt && hasCourts && (
              <div className="card courts-pricing-empty">
                <p>Select a court to view its pricing schedule</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
